// Service interface
export interface Service {
  id: string; // Added ID field
  title: string;
  description: string;
  icon: string;
  // Refined categories: Added sub-categories for planning
  category: 'insurance' | 'savings_retirement' | 'business_planning' | 'estate_legacy' | 'advanced_wealth';
  benefits?: string[];
  details?: string;
}

// --- Service Definitions (Grouped Logically, Category Updated) ---

// Helper function to create URL slugs (moved to top for use in definitions)
export const slugify = (text: string) => {
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, '-')           // Replace spaces with -
    .replace(/[^\w-]+/g, '')        // Remove all non-word chars except - (keeps numbers, letters, underscore, hyphen)
    .replace(/--+/g, '-')         // Replace multiple - with single -
    .replace(/^-+/, '')           // Trim - from start of text
    .replace(/-+$/, '');          // Trim - from end of text
};

// == INSURANCE SERVICES ==
export const insuranceServices: Service[] = [
  {
    id: slugify("Term Life Insurance"),
    title: "Term Life Insurance",
    description: "Affordable coverage for a specific period (e.g., 10, 20, 30 years). Ideal for covering temporary needs like mortgages or income replacement.",
    icon: "⏳",
    category: "insurance",
    benefits: [
      "Lower initial cost compared to permanent insurance.",
      "Provides substantial coverage for specific timeframes.",
      "Simple and easy to understand.",
      "Can often be converted to permanent insurance later."
    ],
    details: "Term life insurance provides a death benefit if the insured person passes away during the specified term. Premiums are typically level for the term length. It does not build cash value. It's essential for protecting dependents from financial hardship due to premature death, ensuring debts are paid and income is replaced."
  },
  {
    id: slugify("Disability Insurance"),
    title: "Disability Insurance",
    description: "Protects your income if you become disabled and unable to work due to illness or injury.",
    icon: "🩹",
    category: "insurance",
    benefits: [
      "Replaces a portion of your lost income.",
      "Helps cover living expenses and bills during disability.",
      "Protects your savings and retirement plans.",
      "Provides peace of mind during recovery."
    ],
    details: "Disability insurance provides a monthly benefit if you are unable to perform the duties of your occupation due to sickness or accident. Policies vary in terms of benefit amount, waiting period (elimination period), benefit period, and definition of disability. Crucial for anyone relying on their income."
  },
  {
    id: slugify("Critical Illness Insurance"),
    title: "Critical Illness Insurance",
    description: "Provides a lump-sum, tax-free payment if you are diagnosed with a covered critical illness (e.g., cancer, heart attack, stroke).",
    icon: "❤️‍🔥",
    category: "insurance",
    benefits: [
      "Financial support during a major health crisis.",
      "Funds can be used for any purpose (medical costs, travel, income replacement).",
      "Reduces financial stress, allowing focus on recovery.",
      "Benefit is paid regardless of ability to work."
    ],
    details: "Critical illness insurance pays a one-time lump sum upon diagnosis of a specified serious illness, provided the survival period is met. This payout helps cover costs not typically covered by provincial health plans or disability insurance, easing financial burdens during a difficult time."
  },
   {
     id: slugify("Group Benefits Policies"),
     title: "Group Benefits Policies",
     description: "Insurance plans offered through employers or associations, covering health, dental, life, disability, and more for members.",
     icon: "👥",
     category: "insurance", // Grouped under Insurance
     benefits: [
        "Cost-effective way for employees/members to access benefits.",
        "Attracts and retains talent for businesses.",
        "Often includes a range of coverages (health, dental, life, LTD, STD).",
        "Simpler underwriting for individuals within the group."
     ],
     details: "Group benefits provide a package of insurance coverages to a group of people, typically employees of a company. We help businesses design, implement, and manage group plans tailored to their needs and budget, covering aspects like extended health care, prescription drugs, dental care, vision care, life insurance, accidental death & dismemberment (AD&D), short-term disability (STD), and long-term disability (LTD)."
   }, 
   {
    id: slugify("Whole Life Insurance"),
    title: "Whole Life Insurance",
    description: "Lifelong coverage with guaranteed premiums, death benefit, and cash value growth.",
    icon: "🏦",
    category: "insurance",
    benefits: [
      "Guaranteed lifelong protection.",
      "Guaranteed level premiums.",
      "Builds tax-deferred cash value.",
      "Potential access to cash value via loans or withdrawals."
    ],
    details: "Whole life insurance provides coverage for your entire life, as long as premiums are paid. It features guaranteed premiums that never increase, a guaranteed death benefit, and a guaranteed cash value component that grows tax-deferred over time. Often used for estate planning and long-term security."
   },
   {
    id: slugify("Participating Whole Life Insurance"),
    title: "Participating Whole Life Insurance",
    description: "Whole life insurance that may earn dividends from the insurer, potentially increasing the death benefit and cash value.",
    icon: "✨", 
    category: "insurance",
    benefits: [
      "All benefits of Whole Life (guarantees).",
      "Potential for dividends to enhance cash value and death benefit.",
      "Dividends are not guaranteed but offer growth potential.",
      "Dividends can be taken in cash, used to reduce premiums, buy more coverage, or left to accumulate."
    ],
    details: "Participating whole life is a type of whole life policy where the policyholder is eligible to receive dividends declared by the insurance company based on its financial performance. These dividends can significantly enhance policy values over the long term, making it a powerful tool for wealth accumulation and estate planning."
   },
   {
     id: slugify("Universal Life Insurance"),
     title: "Universal Life Insurance",
     description: "Flexible permanent insurance offering adjustable premiums and death benefits, with investment options for cash value growth.",
     icon: "⚖️",
     category: "insurance",
     benefits: [
       "Lifelong coverage with premium flexibility.",
       "Choice of investment options for cash value growth.",
       "Ability to adjust death benefit amount (within limits).",
       "Tax-deferred cash value growth."
     ],
     details: "Universal Life (UL) insurance offers flexibility. You can often adjust premium payments and the death benefit amount. The cash value grows based on selected investment options (guaranteed interest accounts or market-linked options), offering potential for higher growth but also carrying investment risk. UL is often used for tax-sheltered investment growth and estate planning."
   }
];


// == SAVINGS & RETIREMENT SERVICES ==
export const savingsRetirementServices: Service[] = [
  {
    id: slugify("Tax-Free Savings Account (TFSA)"),
    title: "Tax-Free Savings Account (TFSA)",
    description: "Save and invest money where investment income, capital gains, and withdrawals are tax-free.",
    icon: "🇨🇦",
    category: "savings_retirement",
    benefits: [
      "Tax-free growth and withdrawals.",
      "Flexible - use for short-term or long-term goals.",
      "Contribution room carries forward.",
      "Wide range of investment options allowed."
    ],
    details: "A TFSA allows Canadians (18+) to set money aside in eligible investments and watch those savings grow tax-free throughout their lifetime. Contributions are made with after-tax dollars, but all investment income and withdrawals are tax-free. Essential tool for any savings goal."
  },
  {
    id: slugify("Registered Retirement Savings Plan (RRSP)"),
    title: "Registered Retirement Savings Plan (RRSP)",
    description: "Save for retirement with tax-deductible contributions and tax-deferred growth.",
    icon: "👴👵",
    category: "savings_retirement",
    benefits: [
      "Tax deduction for contributions reduces current income tax.",
      "Investment growth is tax-deferred until withdrawal.",
      "Crucial tool for long-term retirement planning.",
      "Can be used for Home Buyers' Plan or Lifelong Learning Plan."
    ],
    details: "An RRSP is a government-registered plan designed to help Canadians save for retirement. Contributions are tax-deductible, lowering your taxable income today. Investments inside the RRSP grow tax-deferred until you withdraw funds, typically in retirement when you may be in a lower tax bracket."
  },
  {
    id: slugify("Registered Retirement Income Fund (RRIF)"),
    title: "Registered Retirement Income Fund (RRIF)",
    description: "Convert your RRSP savings into retirement income. Continues tax-deferred growth.",
    icon: "➡️💰",
    category: "savings_retirement",
    benefits: [
      "Provides a regular stream of income in retirement.",
      "Investments continue to grow tax-deferred.",
      "Mandatory minimum withdrawal schedule.",
      "Various investment options available within the RRIF."
    ],
    details: "A RRIF is the primary way to draw income from your RRSP savings in retirement. You must convert your RRSP to a RRIF (or annuity) by the end of the year you turn 71. You must withdraw a minimum amount each year, which is taxed as income, but the remaining funds continue to grow tax-deferred."
  },
  {
    id: slugify("High-Interest Savings Accounts (HISA)"),
    title: "High-Interest Savings Accounts (HISA)",
    description: "Secure savings accounts offering competitive interest rates, ideal for emergency funds or short-term goals.",
    icon: "💲",
    category: "savings_retirement",
    benefits: [
      "Safety of principal (often CDIC insured up to limits).",
      "Earns higher interest than standard savings accounts.",
      "Funds are typically highly accessible.",
      "Good for risk-averse savings."
    ],
    details: "HISAs provide a safe place to park cash while earning a better interest rate than traditional chequing or savings accounts. Ideal for emergency funds, down payment savings, or holding cash short-term before investing."
  },
  {
    id: slugify("Annuities"),
    title: "Annuities",
    description: "Insurance contracts providing a guaranteed income stream for a set period or for life, often used in retirement.",
    icon: " G ", 
    category: "savings_retirement",
    benefits: [
       "Predictable, guaranteed income payments.",
       "Protection against outliving your savings (life annuities).",
       "Can simplify retirement income planning.",
       "Various types available (term certain, life, joint life)."
    ],
    details: "An annuity is a contract with an insurance company where you pay a lump sum (or series of payments) in exchange for guaranteed regular income payments starting immediately or in the future. Annuities can provide certainty and security for retirement income needs."
   }
];


// == PLANNING SERVICES (Broken down into sub-categories) ==

// === Business Planning ===
export const businessPlanningServices: Service[] = [
  {
    id: slugify("Buy-Sell Agreement Funding"),
    title: "Buy-Sell Agreement Funding",
    description: "Use life insurance to fund buy-sell agreements, ensuring business continuity and fair value for departing owners/heirs.",
    category: "business_planning",
    icon: "🤝",
    benefits: [
      "Guarantees funds are available for buyout, preventing financial strain.",
      "Protects business from forced sales or unwanted partners.",
      "Provides clarity and certainty for all owners.",
      "Life insurance is a cost-effective funding solution."
    ],
    details: "A properly funded buy-sell agreement ensures business continuity by providing the capital necessary for surviving owners to purchase a deceased or disabled owner's interest, while providing fair market value to the departing owner's family. Life insurance provides a tax-efficient funding mechanism."
  },
  {
    id: slugify("Key Person Insurance"),
    title: "Key Person Insurance",
    description: "Protect your business from financial loss resulting from the death or disability of a critical employee or owner.",
    category: "business_planning",
    icon: "🔑",
    benefits: [
      "Provides funds to cover lost profits or replacement costs.",
      "Helps maintain business stability during transition.",
      "Reassures creditors and stakeholders of business continuity.",
      "Can fund buyout of a key person's shares if needed."
    ],
    details: "Key person insurance provides funds to the business upon the death or disability of an indispensable individual. This helps cover lost revenue, recruitment costs, debt repayment, or fund a buyout, ensuring business stability during a critical transition."
  },
  {
    id: slugify("Group Benefits Policies"), // Re-using ID from insurance section, assuming it's the same core service
    title: "Group Benefits Policies",
    description: "Comprehensive employee benefits plans (health, dental, life, disability) to attract talent and support your workforce.",
    icon: "👥",
    category: "business_planning",
    benefits: [
      "Enhances employee recruitment and retention.",
      "Provides tax advantages for the business.",
      "Supports employee well-being and productivity.",
      "Customizable to fit business size and budget."
    ],
    details: "Group Benefits Policies are insurance plans offered through a business or association, covering a range of benefits like extended health care, dental, vision, life insurance, and disability. We design tailored plans that meet employee needs while optimizing cost and tax efficiency for the employer."
  }
];

// === Estate & Legacy Planning ===
export const estateLegacyServices: Service[] = [
  {
    id: slugify("Estate Preservation"),
    title: "Estate Preservation",
    description: "Use life insurance and planning techniques to provide liquidity for estate taxes and costs, protecting assets for heirs.",
    category: "estate_legacy",
    icon: "🏛️",
    benefits: [
      "Reduces or eliminates estate taxes and probate fees.",
      "Ensures assets pass to intended beneficiaries.",
      "Can provide liquidity to cover final expenses and taxes.",
      "Protects family wealth from erosion."
    ],
    details: "Estate preservation focuses on minimizing taxes and settlement costs upon death. Life insurance provides immediate, tax-free cash to cover capital gains taxes, probate fees, and other liabilities, preventing the forced sale of assets like businesses, farms, or cottages."
  },
  {
    id: slugify("Charitable Legacy Planning"),
    title: "Charitable Legacy Planning",
    description: "Leverage life insurance to make significant future charitable donations tax-efficiently, maximizing philanthropic impact.",
    category: "estate_legacy",
    icon: "🎁",
    benefits: [
      "Significant tax credits for your estate.",
      "Creates a lasting legacy with causes you care about.",
      "Can reduce overall estate tax burden.",
      "Flexible options (donor-advised funds, foundations, direct gifts)."
    ],
    details: "Using life insurance for charitable giving allows individuals to make a substantial future gift with relatively smaller premium payments. The death benefit provides a large donation, and the estate often receives a significant tax credit, reducing final taxes."
  }
];

// === Advanced Wealth Strategies ===
export const advancedWealthServices: Service[] = [
  {
    id: slugify("Corporate-Owned Life Insurance"),
    title: "Corporate-Owned Life Insurance",
    description: "Utilize corporate-owned permanent life insurance for tax-sheltered investment growth and efficient wealth transfer via the CDA.",
    category: "advanced_wealth",
    icon: "💰",
    benefits: [
      "Tax-sheltered growth of cash value within the policy.",
      "Death benefit creates a Capital Dividend Account (CDA) credit.",
      "Allows tax-free distribution to shareholders' estates.",
      "Effective for passive investment within a corporation."
    ],
    details: "This strategy involves a corporation purchasing permanent life insurance on a shareholder. The cash value grows tax-sheltered, and the death benefit creates a Capital Dividend Account (CDA) credit, allowing tax-free distribution of funds to shareholders' estates. Often used for passive investment and estate planning within a corporation."
  },
  {
    id: slugify("Insured Retirement Strategy (IRS)"),
    title: "Insured Retirement Strategy (IRS)",
    description: "Supplement retirement income tax-efficiently by borrowing against a permanent life insurance policy's cash value.",
    category: "advanced_wealth",
    icon: "🎯",
    benefits: [
      "Creates a tax-free income stream in retirement.",
      "Policy cash value grows tax-deferred.",
      "Death benefit protects family or estate.",
      "Flexible access to funds without taxable withdrawals."
    ],
    details: "The IRS allows policyholders to access the cash value of their permanent life insurance policy via collateral loans during retirement. These loan proceeds are generally received tax-free, providing supplemental income while the death benefit remains intact (less the loan balance) for beneficiaries."
  },
  {
    id: slugify("Immediate Financing Arrangement (IFA)"),
    title: "Immediate Financing Arrangement (IFA)",
    description: "Sophisticated strategy using corporately owned life insurance as collateral for immediate loans, primarily for HNW business/farm owners.",
    category: "advanced_wealth",
    icon: "📊",
    benefits: [
      "Extracts surplus corporate capital with minimal tax.",
      "Provides immediate liquidity for personal or business use.",
      "Life insurance death benefit eventually replaces borrowed capital tax-free.",
      "Can fund retirement income or major purchases."
    ],
    details: "The IFA allows corporations to secure financing by assigning a high-cash-value life insurance policy as collateral. This unlocks capital for investment or succession while leveraging the tax advantages of the insurance policy. Suitable for specific high-net-worth situations."
  },
  {
    id: slugify("Giant TFSA (Tax-Exempt Policies)"),
    title: "Giant TFSA (Tax-Exempt Policies)",
    description: "Uses overfunded life insurance as a 'super TFSA' for high-net-worth individuals who have maxed out registered accounts.",
    icon: "🛡️",
    category: "advanced_wealth",
    benefits: [
      "Tax-free growth on investments within the policy.",
      "Tax-free access to funds via policy loans or withdrawals.",
      "No contribution limits like a standard TFSA.",
      "Death benefit provides additional value."
    ],
    details: "Giant TFSA refers to using a permanent life insurance policy as a tax-exempt investment vehicle for excess capital after maxing out RRSPs and TFSAs. Premiums are overfunded to maximize cash value growth, which is sheltered from tax. Funds can be accessed tax-free through policy loans, mimicking TFSA benefits but on a larger scale."
  }
];


// --- DEPRECATED: Old Groupings (Keep for reference temporarily if needed, then remove) ---
// export const foundationalProtection: Service[] = [ ... ];
// export const permanentInsurance: Service[] = [ ... ];
// export const savingsAndInvestment: Service[] = [ ... ];
// export const advancedStrategies: Service[] = [ ... ];
// export const coreFocusAreas: Service[] = [ ... ];


// --- Updated Service Categories for Navigation/Filtering ---
// Reflects the new sub-categories for Planning
export const serviceCategories = [
  { id: 'insurance', label: 'Insurance', icon: '🛡️' },
  { id: 'savings_retirement', label: 'Savings & Retirement', icon: '💰' },
  { id: 'business_planning', label: 'Business Planning', icon: '💼' },
  { id: 'estate_legacy', label: 'Estate & Legacy', icon: '🏛️' },
  { id: 'advanced_wealth', label: 'Advanced Wealth', icon: '✨' }, // Consider a different icon
  // { id: 'all', label: 'All Services', icon: '🎯' }, // Keep 'all' if needed for filtering UI
];

// --- Combined List for Service Explorer/Display ---
// Now combines the new sub-categories
export const allServicesForExplorer: Service[] = [
  ...insuranceServices,
  ...savingsRetirementServices,
  ...businessPlanningServices,
  ...estateLegacyServices,
  ...advancedWealthServices,
];


// Testimonials data (Keep as is, or update if new testimonials are available)
// TODO: Review testimonial categories if they need alignment with new structure
export const testimonials = [
  {
    quote: "PCM's business advisory services helped us restructure our operations and increase profitability by 32% within the first year.",
    author: "Michael T.",
    position: "CEO, Manufacturing Company",
    category: "business"
  },
  {
    quote: "The agricultural finance solutions provided by PCM were tailored perfectly to our farm's unique needs. We've been able to expand operations while managing risk effectively.",
    author: "Sarah L.",
    position: "Owner, Family Farm",
    category: "agriculture"
  },
  {
    quote: "Their wealth management approach is truly innovative. The tax-exempt policy strategy they recommended has outperformed my traditional investments significantly.",
    author: "Robert J.",
    position: "Retired Business Owner",
    category: "wealth" // Could also fit 'advanced' now
  },
  // Consider adding testimonials related to basic protection or savings if available
];

// Calculated data for wealth growth chart (Corporate Taxable vs. Insurance Strategy)
// Assumptions: Initial $500k, Annual $500k contribution/premium, 20 years.
// Taxable: 6% gross return, 50% tax = 3% net return.
// Insurance: 6.2% net return (tax-sheltered).
export const wealthGrowthData = [
  { year: 0,  'Taxable Investment': 500000,  'Corp-Owned Insurance': 500000 }, // Updated Key
  { year: 1,  'Taxable Investment': 1015000, 'Corp-Owned Insurance': 1031000 },
  { year: 2,  'Taxable Investment': 1545450, 'Corp-Owned Insurance': 1594922 },
  { year: 3,  'Taxable Investment': 2091814, 'Corp-Owned Insurance': 2193807 },
  { year: 4,  'Taxable Investment': 2654568, 'Corp-Owned Insurance': 2830002 },
  { year: 5,  'Taxable Investment': 3234205, 'Corp-Owned Insurance': 3505462 },
  { year: 6,  'Taxable Investment': 3831231, 'Corp-Owned Insurance': 4222801 },
  { year: 7,  'Taxable Investment': 4446168, 'Corp-Owned Insurance': 4984615 },
  { year: 8,  'Taxable Investment': 5079553, 'Corp-Owned Insurance': 5793661 },
  { year: 9,  'Taxable Investment': 5731940, 'Corp-Owned Insurance': 6652868 },
  { year: 10, 'Taxable Investment': 6403898, 'Corp-Owned Insurance': 7565346 },
  { year: 11, 'Taxable Investment': 7096015, 'Corp-Owned Insurance': 8534397 },
  { year: 12, 'Taxable Investment': 7808895, 'Corp-Owned Insurance': 9563530 },
  { year: 13, 'Taxable Investment': 8543162, 'Corp-Owned Insurance': 10656469 },
  { year: 14, 'Taxable Investment': 9299457, 'Corp-Owned Insurance': 11817169 },
  { year: 15, 'Taxable Investment': 10078441,'Corp-Owned Insurance': 13050053 },
  { year: 16, 'Taxable Investment': 10880794,'Corp-Owned Insurance': 14359156 },
  { year: 17, 'Taxable Investment': 11707218,'Corp-Owned Insurance': 15749424 },
  { year: 18, 'Taxable Investment': 12558435,'Corp-Owned Insurance': 17225888 },
  { year: 19, 'Taxable Investment': 13435188,'Corp-Owned Insurance': 18793903 },
  { year: 20, 'Taxable Investment': 14338244,'Corp-Owned Insurance': 20459125 }
];

// Note: slugify function moved to the top
